/**
 * Report Writer required header fields (REQ-005, FE-5-4)
 * Location: frontend/src/utils/reportRequiredFields.js
 *
 * The required list follows the form each report type actually shows
 * (components/report-writer/components/forms/CompactFormView.js): a Key
 * Decision has a Decision Maker, a Knowledge Gap has Contributors. Requiring
 * `contributors` for a KD blocked Finalize, because the KD form never writes it.
 */

// Same order as before FE-5-4, so the error message reads the same; only the
// person field differs by type.
const REQUIRED_FIELDS = {
  knowledge_gap: ['title', 'owner', 'contributors', 'project_name', 'kg_number', 'status'],
  key_decision: ['title', 'owner', 'decision_maker', 'project_name', 'kd_number', 'status'],
};

const FIELD_LABELS = {
  kg_number: 'KG #',
  kd_number: 'KD #',
};

/**
 * Required fields for a report type. Anything other than 'knowledge_gap' is
 * treated as a Key Decision, matching how ReportWriterV2 picks its sections.
 *
 * @param {string} reportType - 'key_decision' | 'knowledge_gap'
 * @returns {string[]} field keys in display order
 */
export const getRequiredFields = (reportType) => [
  ...(reportType === 'knowledge_gap' ? REQUIRED_FIELDS.knowledge_gap : REQUIRED_FIELDS.key_decision),
];

/**
 * Required fields that are missing or blank in formData.
 *
 * @param {Object} formData - Report Writer form state
 * @param {string} reportType - 'key_decision' | 'knowledge_gap'
 * @returns {string[]} missing field keys
 */
export const getMissingRequiredFields = (formData, reportType) =>
  getRequiredFields(reportType).filter((field) => String(formData?.[field] ?? '').trim() === '');

/**
 * User-facing label for a field key, e.g. 'decision_maker' → 'Decision Maker'.
 *
 * @param {string} field - field key
 * @returns {string} label
 */
export const getRequiredFieldLabel = (field) =>
  FIELD_LABELS[field] ||
  field.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
